import { GridLogicOperator, type GridFilterItem, type GridFilterModel } from '@mui/x-data-grid-pro';

/**
 * Groups of filter rows of the issue grid (PT-7187): `(Process is QA AND
 * Stage is execution) OR Reported by is Julian`. MUI's filter model is a flat
 * list of rows joined by one operator and has no groups (mui/mui-x#4217), but
 * it keeps the extra fields of its rows and of the model. So a row names its
 * group (`groupId`) and the model holds each group's operator: presets, the
 * saved grid state and the filter count keep working on the same model, and
 * a model without groups is the flat one it was.
 *
 * One level of groups: the grid's operator joins the rows outside groups and
 * the groups, each group's operator joins its own rows.
 */
export interface IssueFilterRow extends GridFilterItem {
  /** The group the row belongs to; none for a row joined by the grid's operator. */
  groupId?: string;
}

export interface IssueFilterModel extends GridFilterModel {
  items: IssueFilterRow[];
  /** The operator joining the rows of each group; AND when not set. */
  groupLogicOperators?: Record<string, GridLogicOperator>;
}

/** A row outside groups, or a group with its rows, in the order of the model's rows. */
export type IssueFilterPanelNode =
  | { kind: 'row'; row: IssueFilterRow }
  | { kind: 'group'; groupId: string; logicOperator: GridLogicOperator; rows: IssueFilterRow[] };

export function groupLogicOperatorOf(model: IssueFilterModel, groupId: string): GridLogicOperator {
  return model.groupLogicOperators?.[groupId] ?? GridLogicOperator.And;
}

/** The rows outside groups and the groups, each group where its first row is. */
export function listFilterPanelNodes(model: IssueFilterModel): IssueFilterPanelNode[] {
  const nodes: IssueFilterPanelNode[] = [];
  const groupsById = new Map<string, Extract<IssueFilterPanelNode, { kind: 'group' }>>();

  (model.items ?? []).forEach((row) => {
    if (!row.groupId) {
      nodes.push({ kind: 'row', row });
      return;
    }
    const group = groupsById.get(row.groupId);
    if (group) {
      group.rows.push(row);
      return;
    }
    const newGroup = {
      kind: 'group' as const,
      groupId: row.groupId,
      logicOperator: groupLogicOperatorOf(model, row.groupId),
      rows: [row],
    };
    groupsById.set(row.groupId, newGroup);
    nodes.push(newGroup);
  });

  return nodes;
}

/** Only the operators of groups that still have rows. */
function withoutEmptyGroups(model: IssueFilterModel): IssueFilterModel {
  if (!model.groupLogicOperators) {
    return model;
  }
  const groupIds = new Set(model.items.map(({ groupId }) => groupId).filter(Boolean));
  const groupLogicOperators = Object.fromEntries(
    Object.entries(model.groupLogicOperators).filter(([groupId]) => groupIds.has(groupId))
  );
  return { ...model, groupLogicOperators };
}

export function addFilterRow(model: IssueFilterModel, row: IssueFilterRow): IssueFilterModel {
  return { ...model, items: [...model.items, row] };
}

/** The row edited in place, or added when the model does not have it yet (the panel's starting row). */
export function upsertFilterRow(model: IssueFilterModel, row: IssueFilterRow): IssueFilterModel {
  const hasRow = model.items.some(({ id }) => id === row.id);
  return hasRow
    ? { ...model, items: model.items.map((item) => (item.id === row.id ? row : item)) }
    : addFilterRow(model, row);
}

export function setFilterLogicOperator(model: IssueFilterModel, logicOperator: GridLogicOperator): IssueFilterModel {
  return { ...model, logicOperator };
}

/**
 * A new group holding its first rows, joined by AND. With `wrapGroupId`, the
 * first group also takes the rows outside groups into a group of their own,
 * keeping the operator that joined them, so the rows and the new group are
 * two groups joined by the grid's operator (AND).
 */
export function addFilterGroup(
  model: IssueFilterModel,
  groupId: string,
  firstRows: GridFilterItem[],
  wrapGroupId?: string
): IssueFilterModel {
  const wrapsRows = Boolean(wrapGroupId) && model.items.length > 0 && !model.items.some((row) => row.groupId);
  const keptRows = wrapsRows ? model.items.map((row) => ({ ...row, groupId: wrapGroupId })) : model.items;

  return {
    ...model,
    items: [...keptRows, ...firstRows.map((row) => ({ ...row, groupId }))],
    logicOperator: wrapsRows ? GridLogicOperator.And : model.logicOperator,
    groupLogicOperators: {
      ...model.groupLogicOperators,
      ...(wrapsRows && wrapGroupId && { [wrapGroupId]: model.logicOperator ?? GridLogicOperator.And }),
      [groupId]: GridLogicOperator.And,
    },
  };
}

/**
 * A model whose rows all belong to one group has nothing for that group to be
 * joined to: its rows become the rows outside groups, joined by its operator.
 * The same filter, but a row added next joins them under that operator
 * instead of opening a second level (a preset saved with one group, or the
 * other groups removed).
 */
export function withoutLoneGroup(model: IssueFilterModel): IssueFilterModel {
  const [firstRow] = model.items;
  const loneGroupId = firstRow?.groupId;
  if (!loneGroupId || model.items.some(({ groupId }) => groupId !== loneGroupId)) {
    return model;
  }
  return {
    ...model,
    items: model.items.map(({ groupId: _groupId, ...row }) => row),
    logicOperator: groupLogicOperatorOf(model, loneGroupId),
    groupLogicOperators: {},
  };
}

/**
 * A group with one row joins nothing: its row becomes a row outside groups,
 * joined by the grid's operator as the group was. The same filter, without a
 * row drawn as part of a group that has no bracket (what a removed row of a
 * two-row group leaves).
 */
function withoutSingleRowGroups(model: IssueFilterModel): IssueFilterModel {
  const rowCountOfGroup = new Map<string, number>();
  model.items.forEach(({ groupId }) => groupId && rowCountOfGroup.set(groupId, (rowCountOfGroup.get(groupId) ?? 0) + 1));
  const singleRowGroupIds = new Set([...rowCountOfGroup].filter(([, rowCount]) => rowCount === 1).map(([groupId]) => groupId));
  if (!singleRowGroupIds.size) {
    return model;
  }
  const items = model.items.map(({ groupId, ...row }) =>
    groupId !== undefined && !singleRowGroupIds.has(groupId) ? { ...row, groupId } : row
  );
  return withoutEmptyGroups({ ...model, items });
}

/** The model as the panel draws it: no group with one row, and no group that is all there is. */
export function simplifyFilterGroups(model: IssueFilterModel): IssueFilterModel {
  return withoutLoneGroup(withoutSingleRowGroups(model));
}

export function removeFilterRow(model: IssueFilterModel, rowId: IssueFilterRow['id']): IssueFilterModel {
  return simplifyFilterGroups(withoutEmptyGroups({ ...model, items: model.items.filter(({ id }) => id !== rowId) }));
}

export function setFilterGroupLogicOperator(
  model: IssueFilterModel,
  groupId: string,
  logicOperator: GridLogicOperator
): IssueFilterModel {
  return { ...model, groupLogicOperators: { ...model.groupLogicOperators, [groupId]: logicOperator } };
}
